import './virtual-accounts.scss';

import { createStaticVirtualAccount } from '@api/virtual-accounts';
import { ReactComponent as Copy } from '@basqet-dashboard/common/assets/icons/copy.svg';
import { ReactComponent as Share } from '@basqet-dashboard/common/assets/icons/share.svg';
import { Button, Modal, RingLoader } from '@basqet-dashboard/common/components';
import { copyToClipboard, formatErrorMessage } from '@basqet-dashboard/common/utils';
import { useFetchStaticVirtualAccount } from '@hooks/queries';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

const ACCOUNT_BLURB =
  'This account is permanently assigned to your business for Naira payments. Deposited funds will be automatically added to your NGN balance.';

const NgnVirtualAccountModal = ({ onClose, autoCreate }) => {
  const queryClient = useQueryClient();
  const { account, isLoading, isError, notFound, refetch } = useFetchStaticVirtualAccount();
  const [isCreating, setIsCreating] = useState(false);
  const [createdAccount, setCreatedAccount] = useState(null);
  const [justCreated, setJustCreated] = useState(false);
  const autoCreateTriggered = useRef(false);
  const displayAccount = account || createdAccount;

  const handleCreateAccount = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const response = await createStaticVirtualAccount({ currency: 'NGN' });
      const nextAccount = response?.data || null;
      if (nextAccount) setCreatedAccount(nextAccount);
      Promise.resolve(refetch()).catch(() => undefined);
      setJustCreated(true);
    } catch (error) {
      formatErrorMessage(error);
      queryClient.invalidateQueries(['staticVirtualAccount']);
      onClose();
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (!autoCreate) return;
    if (autoCreateTriggered.current) return;
    if (isLoading || isCreating || displayAccount) return;
    if (!notFound) return;
    autoCreateTriggered.current = true;
    handleCreateAccount();
  }, [autoCreate, notFound, isLoading, isCreating, displayAccount]);

  return (
    <Modal onModalClose={onClose} ariaLabel="NGN Collection Account">
      {isCreating ? (
        <GeneratingState />
      ) : isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState onRetry={refetch} />
      ) : displayAccount ? (
        <AccountState account={displayAccount} showConfetti={justCreated} />
      ) : notFound && !autoCreate ? (
        <EmptyState onGenerate={handleCreateAccount} />
      ) : (
        <LoadingState />
      )}
    </Modal>
  );
};

NgnVirtualAccountModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  autoCreate: PropTypes.bool
};

NgnVirtualAccountModal.defaultProps = {
  autoCreate: false
};

const ModalHeader = ({ overline, name, cursive, tone }) => (
  <header className="ngn-va__header">
    <span className={`ngn-va__avatar ngn-va__avatar--${tone}`}>N</span>
    <div className="ngn-va__heading">
      <span className="ngn-va__overline">{overline}</span>
      <h3 className="ngn-va__title">
        {name} <span className="text-cursive">{cursive}</span>
      </h3>
    </div>
  </header>
);

ModalHeader.propTypes = {
  overline: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  cursive: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(['green', 'dark']).isRequired
};

const GeneratingState = () => (
  <>
    <ModalHeader overline="Generating" name="NGN" cursive="Collection Account" tone="dark" />
    <div className="ngn-va__status">
      <RingLoader />
      <p className="ngn-va__status-title">Checking your business profile</p>
      <p className="ngn-va__status-sub">We&apos;re verifying your KYB information.</p>
    </div>
  </>
);

const LoadingState = () => (
  <>
    <ModalHeader
      overline="NGN Collection Account"
      name="Your"
      cursive="Naira account"
      tone="green"
    />
    <div className="ngn-va__status">
      <RingLoader />
      <p className="ngn-va__status-sub">Loading your account details…</p>
    </div>
  </>
);

const ErrorState = ({ onRetry }) => (
  <>
    <ModalHeader
      overline="NGN Collection Account"
      name="Your"
      cursive="Naira account"
      tone="green"
    />
    <p className="ngn-va__blurb">
      We couldn&apos;t load your account details. Please check your connection and try again.
    </p>
    <Button variant="outline" isFull type="button" onClick={onRetry}>
      Try again
    </Button>
  </>
);

ErrorState.propTypes = { onRetry: PropTypes.func.isRequired };

const EmptyState = ({ onGenerate }) => (
  <>
    <ModalHeader
      overline="NGN Collection Account"
      name="Your"
      cursive="Naira account"
      tone="green"
    />
    <p className="ngn-va__blurb">
      Generate a dedicated Naira account to receive payments via bank transfer. {ACCOUNT_BLURB}
    </p>
    <Button isFull type="button" onClick={onGenerate}>
      Generate account
    </Button>
  </>
);

EmptyState.propTypes = { onGenerate: PropTypes.func.isRequired };

const AccountState = ({ account, showConfetti }) => {
  const confettiAnchor = useRef(null);

  useEffect(() => {
    if (!showConfetti) return;
    const anchor = confettiAnchor.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const origin = {
      x: (rect.left + rect.width / 2) / window.innerWidth,
      y: (rect.top + rect.height / 2) / window.innerHeight
    };
    const fire = (particleRatio, opts) =>
      confetti({
        origin,
        spread: 70,
        startVelocity: 35,
        ticks: 200,
        particleCount: Math.floor(160 * particleRatio),
        ...opts
      });
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }, [showConfetti]);

  const handleCopyAll = () => {
    const details = [
      `Account Name: ${account.account_name}`,
      `Bank Name: ${account.bank_name}`,
      `Account Number: ${account.account_number}`
    ].join('\n');
    copyToClipboard('Account details', details);
  };

  const handleShare = async () => {
    const text = `${account.account_name}\n${account.bank_name}\n${account.account_number}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'NGN Collection Account', text });
        return;
      } catch {
        // User dismissed the share sheet, or it is unavailable — fall back to copy.
      }
    }
    copyToClipboard('Account details', text);
  };

  return (
    <div ref={confettiAnchor} data-testid="ngn-account-state">
      <ModalHeader
        overline="NGN Collection Account"
        name="Your"
        cursive="Naira account"
        tone="green"
      />

      <dl className="ngn-va__card">
        <AccountRow label="Account Name" value={account.account_name} />
        <AccountRow label="Bank Name" value={account.bank_name} />
        <AccountRow
          label="Account Number"
          value={account.account_number}
          onCopy={() => copyToClipboard('Account Number', account.account_number)}
        />
      </dl>

      <p className="ngn-va__blurb">{ACCOUNT_BLURB}</p>

      <div className="ngn-va__actions">
        <Button variant="outline" type="button" onClick={handleCopyAll}>
          <Copy /> Copy details
        </Button>
        <Button type="button" onClick={handleShare}>
          <Share /> Share
        </Button>
      </div>
    </div>
  );
};

AccountState.propTypes = {
  account: PropTypes.shape({
    account_name: PropTypes.string,
    bank_name: PropTypes.string,
    account_number: PropTypes.string
  }).isRequired,
  showConfetti: PropTypes.bool
};

AccountState.defaultProps = {
  showConfetti: false
};

const AccountRow = ({ label, value, onCopy }) => (
  <div className="ngn-va__row">
    <dt className="ngn-va__row-label">{label}</dt>
    <dd className="ngn-va__row-value">
      <span>{value}</span>
      {onCopy && (
        <button
          className="ngn-va__copy"
          type="button"
          onClick={onCopy}
          aria-label={`Copy ${label}`}
          title={`Copy ${label}`}>
          <Copy /> COPY
        </button>
      )}
    </dd>
  </div>
);

AccountRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onCopy: PropTypes.func
};

// Sub-section on the NGN wallet card showing the masked account number with a
// COPY affordance. Lives inside the card's NavLink, so spans + stopPropagation
// are required to avoid both an invalid <button>-in-anchor and an unintended
// route change. Click on COPY copies just the number; click on the rest of the
// block opens the modal.
const NgnWalletCardAction = ({ onClick, account }) => {
  if (!account) return null;
  const accountNumber = account.account_number || '';
  const masked = accountNumber ? `•••• ${accountNumber.slice(-4)}` : '••••';

  const openModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    copyToClipboard('Account Number', accountNumber);
  };

  return (
    <span
      className="ngn-va__card-section"
      role="button"
      tabIndex={0}
      onClick={openModal}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') openModal(e);
      }}>
      <span className="ngn-va__card-section-info">
        <span className="ngn-va__card-section-label">NAIRA COLLECTION ACCOUNT</span>
        <span className="ngn-va__card-section-number">{masked}</span>
      </span>
      <span
        className="ngn-va__card-section-copy"
        role="button"
        tabIndex={0}
        aria-label="Copy account number"
        onClick={handleCopy}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleCopy(e);
        }}>
        <Copy /> COPY
      </span>
    </span>
  );
};

NgnWalletCardAction.propTypes = {
  onClick: PropTypes.func.isRequired,
  account: PropTypes.shape({
    account_number: PropTypes.string
  })
};

export { NgnVirtualAccountModal, NgnWalletCardAction };
